import React, { useState, useEffect } from 'react';
import axios from 'utils/axios';
import { Link, useNavigate } from 'react-router-dom';
import { Table } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { enGB, bs } from 'date-fns/locale'; // Use British English for Monday-starting weeks
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Autocomplete,
  Grid,
  CircularProgress,
  IconButton,
  Typography
} from '@mui/material';
import AddRequest from './AddRequest';
import { toast } from 'react-toastify';
import { formatDate } from 'utils/functions';
import Pagination from 'components/Pagination';
import { MdAddCircle, MdClose } from 'react-icons/md';
import { FiFilter } from 'react-icons/fi';
import { t } from 'i18next';




const Requests = () => {

  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [createRequestDialog, setCreateRequestDialog] = useState(false);



  const [nameFilter, setNameFilter] = useState('');
  const [personName, setPersonName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [workflowNumber, setWorkflowNumber] = useState('');
  const [employee, setEmployee] = useState('');

  const [departmentId, setDepartmentId] = useState(1);


  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const itemsPerPage = 10;

  const handleFilterSubmit = () => {
    fetchRequests(1);
    setOpenFilterDialog(false); // Close the filter dialog
  };

  const handleClearFilters = () => {
    setNameFilter('');
    setPersonName('');
    setDateFrom('');
    setDateTo('');
    setWorkflowNumber('');
    setEmployee('');
  };

  const handleCloseDialog = (success = false) => {
    setCreateRequestDialog(false);
    if (success) {
      setCurrentPage(1);
      //toast.success('Your addition was successful!');
      fetchRequests(currentPage);
    }

  };

  const fetchRequests = async (currentPage) => {
    try {

      const response = await axios.get('requests', {
        params: {
          page: currentPage,
          count: itemsPerPage,
          requestName: nameFilter,
          workflowNumber: workflowNumber,
          dateFrom: dateFrom,
          dateTo: dateTo,
          personName: personName,
          employee: employee
          //search: query, 
        },
      });

      setRequests(response.data.items);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));

      setLoading(false);
    } catch (err) {
      toast.error(t('error_fetching_items'));
      setLoading(false);
    }
    /*setRequests(dummyData);
    setLoading(false);*/
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`/requests/getUsers/${departmentId}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };


  useEffect(() => {
    fetchRequests(currentPage);
  }, [currentPage]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
    fetchUsers(); // fetch users right when opening
  };

  if (loading) {
    return <div>{t('loading')}</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'received':
        return 'bg-info-subtle text-primary'; // very light blue background
      case 'inProgress':
        return 'bg-primary-subtle text-primary'; // lighter primary blue
      case 'userInputRequired':
        return 'bg-warning-subtle text-warning'; // light yellow
      case 'rejected':
        return 'bg-danger-subtle text-danger'; // soft red
      case 'done':
        return 'bg-success-subtle text-success'; // soft green
      case 'archived':
        return 'bg-secondary-subtle text-secondary'; // soft gray
      default:
        return 'bg-light text-muted'; // fallback to soft gray
    }
  };


  return (
    <div className="container-fluid mt-4 px-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>{t('requestList')}</h2>

        <div className="d-flex gap-2">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenFilterDialog}
            startIcon={<FiFilter size={18} />}
          >
            {t('filter')}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={setCreateRequestDialog}
            startIcon={<MdAddCircle size={18} />}
          >
            {t('addRequest')}
          </Button>
        </div>
      </div>


      <Dialog
        open={createRequestDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setCreateRequestDialog(false);
            handleCloseDialog();
          }
        }}
        fullWidth
        maxWidth="md"   // 900px
      >
        <DialogTitle sx={{ m: 0, pl: 4 }}>
          {t('addRequest')}
          <IconButton
            aria-label="close"
            onClick={() => handleCloseDialog()}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>
        <DialogContent><AddRequest onSuccess={handleCloseDialog}></AddRequest></DialogContent>
      </Dialog>
      <Dialog
        open={openFilterDialog}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            setOpenFilterDialog(false);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {t('filter_requests')}
          <IconButton
            aria-label="close"
            onClick={() => setOpenFilterDialog(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <MdClose />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, pt: 2 }}>
          <Box mb={2}>
            <TextField
              label={t('search_by_person_name')}
              variant="outlined"
              fullWidth
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
            />
          </Box>
          <Box mb={2}>
            <TextField
              label={t('search_by_name')}
              variant="outlined"
              fullWidth
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </Box>
          <Box mb={2}>
            <TextField
              label={t('search_by_workflow_number')}
              variant="outlined"
              fullWidth
              value={workflowNumber}
              onChange={(e) => setWorkflowNumber(e.target.value)}
            />
          </Box>
          <Box mb={3}>
            <Autocomplete
              options={users}
              // getOptionLabel={(option) => option} // adjust if needed (e.g., `${option.firstName} ${option.lastName}`)
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('search_by_employee')}
                  variant="outlined"
                  fullWidth
                />
              )}
              value={employee}
              onChange={(event, newValue) => {
                setEmployee(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Box>

          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom>
              {t('date')}
            </Typography>
            <Box display="flex" gap={2}>
              <DatePicker
                selected={dateFrom}
                onChange={(date) => setDateFrom(date)}
                locale={bs}
                dateFormat="dd.MM.yyyy"
                placeholderText={t('start_date')}
                className="form-control"
              />
              <DatePicker
                selected={dateTo}
                onChange={(date) => setDateTo(date)}
                locale={bs}
                dateFormat="dd.MM.yyyy"
                placeholderText={t('end_date')}
                className="form-control"
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button color="error" onClick={handleClearFilters}>
            {t('clear_all_filters')}
          </Button>
          <Box>
            <Button onClick={() => setOpenFilterDialog(false)} sx={{ mr: 2 }}>
              {t('cancel')}
            </Button>
            <Button onClick={handleFilterSubmit} variant="contained">
              {t('search')}
            </Button>
          </Box>
        </DialogActions>

      </Dialog>

      <br></br>
      <table className="table w-full text-sm text-left border-collapse">
  <thead className="border-b">
    <tr>
      <th>{t('title')}</th>
      <th>{t('requestor')}</th>
      <th>{t('date')}</th>
      <th>{t('approved')}</th>
      <th>{t('status')}</th>
      <th>{t('office')}</th>
      <th>{t('assignedUser')}</th>
      <th>{t('protocol_number')}</th>
      <th>{t('actions')}</th>
    </tr>
  </thead>
  <tbody>
    {requests.map((request) => (
      <tr key={request.id} className="border-b hover:bg-gray-50">
        <td>{request.name}</td>
        <td>{request.personName}</td>
        <td>{formatDate(request.requestDate)}</td>
        <td>
          <span
            className={`inline-block px-2 py-1 text-xs rounded ${
              request.approved ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-800'
            }`}
          >
            {t(request.approved ? 'yes' : 'no')}
          </span>
        </td>
        <td>
          <span
            className={`inline-block px-2 py-1 text-xs rounded ${getStatusClass(
              request.requestStatus
            )}`}
          >
            {t(`${request.requestStatus}`)}
            {request.addition && (
              <span
                title={t('new_informations_available')}
                className="ml-1 text-orange-500"
              >
                🔔
              </span>
            )}
          </span>
        </td>
        <td>{request.office}</td>
        <td>{request.assignedUser}</td>
        <td>{request.workflowNumber}</td>
        <td>
          <Link
            to={`/requests/${request.id}`}
            className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
          >
            {t('view_details')}
          </Link>
        </td>
      </tr>
    ))}
  </tbody>
</table>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        loading={loading}
      />
    </div>

  );
};

export default Requests;

